import { Alert, Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Row, Segmented } from 'antd';
import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// components
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FileInput from 'components/fileInput/fileInput';
import FormElement from 'components/formElement/formElement';
import PageTitle from 'components/title/title';
import DatePickerElement from 'components/datePicker/datePicker';
import ActionCostComponent from 'modules/main/actions/cost';
import MapComponent from '../../../../../components/map/map';

// services

// styles
import googleMeetLogo from 'assets/icons/google_meet.svg';

// models
import { AssemblyLocation, AssemblyType, Decision, DecisionFormItem, Staff } from '../models/assemblies';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import VerificationModal from '../../verificationModal';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import NotificationService from 'core/services/notification';
import { onCreateAssembly, onGetAssemblyChairmanCandidates, onGetStaff } from '../services/assembliesServices';
import { onGetServiceCost } from '../../commonServices';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useDispatch } from 'react-redux';
import { subtractPoints } from 'stores/useCompanyStore';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';
import AssemblyPointsElement from '../components/points';
import SelectElement from 'components/selectElement/selectElement';

interface ActionAssembliesCreatePageProps {}

const ActionAssembliesCreatePage: FunctionComponent<ActionAssembliesCreatePageProps> = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [assemblyForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();
	const [staff, setStaff] = useState<{ label: string; value: number; meta: any }[]>([]);
	const [chairmanCandidates, setChairmanCandidates] = useState<{ label: string; value: number; meta: any }[]>([]);
	const [isChairmanRequired, setIsChairmanRequired] = useState(false);

	const [assemblyType, setAssemblyType] = useState<AssemblyType>('Ordinary');
	const [assemblyLocation, setAssemblyLocation] = useState<AssemblyLocation[]>(['Online']);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState(false);

	const assemblyTypeOptions = [
		{
			value: 'Ordinary',
			label: (
				<p className="fw-medium mb-0" style={{ fontSize: 15 }}>
					{t('ACTIONS.ORDINARY')}
				</p>
			),
		},
		{
			value: 'Extraordinary',
			label: (
				<p className="fw-medium mb-0" style={{ fontSize: 15 }}>
					{t('ACTIONS.EXTRAORDINARY')}
				</p>
			),
		},
	];

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			setIsLoading(true);

			await Promise.all([getServiceCost(), getStaff(), getChairmanCandidates()]);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			navigate('/dashboard/actions/assemblies');
		}
	};

	const getServiceCost = async () => {
		try {
			const response = await onGetServiceCost('assemblies');

			if (!response?.success?.can_access) {
				navigate('/dashboard/actions/assemblies');
				NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.CANT_ACCESS_ASSEMBLIES_SERVICE'));
				return;
			}

			setServiceCost(response?.success);
		} catch (error) {}
	};

	const getStaff = async () => {
		try {
			const response = await onGetStaff();

			setStaff(response.map((staff) => ({ label: staff.name, value: staff.id, meta: { type: staff.type, signature: staff.signature } })));
		} catch (error) {}
	};

	const getChairmanCandidates = async () => {
		try {
			const response = await onGetAssemblyChairmanCandidates();

			if (!response?.has_active_board || !response?.chairman_required) return;

			const options = response.members.map((member) => ({
				label: `${(i18n.language === 'en' ? member.name_en : member.name) || member.name} - ${member.translated_position?.[i18n.language as 'en' | 'ar'] ?? member.position}`,
				value: member.id,
				meta: { type: member.type },
			}));

			setChairmanCandidates(options);
			setIsChairmanRequired(true);

			const defaultMember = response.members.find((member) => member.is_default);

			if (defaultMember) assemblyForm.setFieldsValue({ chairman_id: defaultMember.id, chairman_type: defaultMember.type });
		} catch (error) {}
	};

	const onChangeAssemblyLocation = (location: AssemblyLocation, checked: boolean) => {
		if (checked) {
			setAssemblyLocation([...assemblyLocation, location]);
			assemblyForm.setFieldsValue({ location_type: [...assemblyLocation, location] });
		} else {
			const tempLocation = [...assemblyLocation];
			tempLocation.splice(tempLocation.indexOf(location), 1);
			setAssemblyLocation(tempLocation);
			assemblyForm.setFieldsValue({ location_type: tempLocation });
		}
	};

	const onValidateForm = async () => {
		setVerificationModalOpen(true);
	};

	const onSubmit = async ({ key, method }: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();

			payload.append('verified_using', method);
			payload.append(method, key);
			payload.append('type', assemblyType);
			payload.append('assembly_quorum_percentage', assemblyForm.getFieldValue('assembly_quorum_percentage'));
			payload.append('location_type', assemblyLocation.length == 2 ? 'Both' : assemblyLocation[0]);
			payload.append('datetime', dayjs(assemblyForm.getFieldValue('datetime')).format('YYYY-MM-DD hh:mm a'));

			payload.append('general_secretary_id', assemblyForm.getFieldValue('general_secretary_id'));
			payload.append('general_secretary_type', assemblyForm.getFieldValue('general_secretary_type'));
			payload.append('vote_collector_id', assemblyForm.getFieldValue('vote_collector_id'));
			payload.append('vote_collector_type', assemblyForm.getFieldValue('vote_collector_type'));

			if (isChairmanRequired) {
				payload.append('chairman_id', assemblyForm.getFieldValue('chairman_id'));
				payload.append('chairman_type', assemblyForm.getFieldValue('chairman_type'));
			}

			if (assemblyLocation.includes('Physical')) {
				payload.append('physical_lat', assemblyForm.getFieldValue('physical_lat'));
				payload.append('physical_lng', assemblyForm.getFieldValue('physical_lng'));
				payload.append('physical_address', assemblyForm.getFieldValue('physical_address'));
				payload.append('physical_address_description', assemblyForm.getFieldValue('physical_address_description'));
			}

			assemblyForm.getFieldValue('files')?.forEach((fileObj: { file: File; title: string }, index: number) => {
				payload.append(`files[${index}][file]`, fileObj.file);
				payload.append(`files[${index}][title]`, fileObj.title);
			});

			assemblyForm.getFieldValue('decisions')?.forEach((point: DecisionFormItem, index: number) => {
				payload.append(`decisions[${index}][ar]`, point.translated_title.ar ?? '');
				payload.append(`decisions[${index}][en]`, point.translated_title.en ?? '');
				payload.append(`decisions[${index}][quorum_percentage]`, point.quorum_percentage.toString());
				payload.append(`decisions[${index}][type]`, point.type);
				payload.append(`decisions[${index}][abstention_type]`, point.abstention_type);

				if (point.abstention_type === 'specific_shareholders')
					point.abstaining_shareholder_ids?.forEach((shareholder_id: string, shIndex: number) => {
						payload.append(`decisions[${index}][abstaining_shareholder_ids][${shIndex}]`, shareholder_id);
					});
			});

			await onCreateAssembly(payload);

			if (serviceCost?.payment_method === 'points') dispatch(subtractPoints(serviceCost.payment_details.points_required || 0));

			setIsSubmitting(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ASSEMBLY_CREATED'));
			navigate('/dashboard/actions/assemblies');
		} catch (error: any) {
			console.log(error);
			setIsSubmitting(false);

			const errorMessage = error?.response?.data?.data?.errors || [error?.response?.data?.message];
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Card>
						<Form form={assemblyForm} onFinish={onValidateForm} initialValues={{ files: [{}], assembly_quorum_percentage: 25 }} scrollToFirstError>
							<ComponentHeader title={t('ACTIONS.ASSEMBLIES')} subtitle={t('ACTIONS.ASSEMBLY_FILL_TO_REVIEW')} />

							<div>
								<Segmented
									block
									className="custom white"
									size="middle"
									options={assemblyTypeOptions}
									onChange={(value) => {
										setAssemblyType(value as AssemblyType);
										assemblyForm.validateFields(['assembly_quorum_percentage']);
									}}
								/>
							</div>

							<Row gutter={[16, 16]} className="mt-5">
								<Col span={24} md={12}>
									<FormNumberElement
										rules={[
											{ required: true, message: t('COMMON.FIELD_REQUIRED') },
											{
												type: 'number',
												min: assemblyType == 'Ordinary' ? 25 : 50,
												message: t('ACTIONS.QUORUM_MUST_BE_ATLEAST', { num: assemblyType == 'Ordinary' ? 25 : 50 }),
											},
										]}
										name="assembly_quorum_percentage"
										label={t('ACTIONS.ATTENDANCE_QUORUM')}
										min={0}
										max={100}
										precision={2}
										addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
									/>
								</Col>

								<Col span={24} md={12}>
									<DatePickerElement
										name="datetime"
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										label={t('ACTIONS.CHOOSE_ASSEMBLY_DATE')}
										minDate={dayjs()}
										showTime={{ format: 'hh:mm a' }}
									/>
								</Col>
							</Row>

							<Row gutter={[16, 16]} className="mt-5">
								<Col span={24} md={isChairmanRequired ? 8 : 12}>
									<SelectElement
										options={staff}
										label={t('ACTIONS.GENERAL_SECRETARY_NAME')}
										name="general_secretary_id"
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										onChange={(_, meta) => assemblyForm.setFieldValue('general_secretary_type', meta?.type)}
									/>
									<Form.Item name="general_secretary_type" hidden noStyle />
								</Col>

								<Col span={24} md={isChairmanRequired ? 8 : 12}>
									<SelectElement
										options={staff}
										label={t('ACTIONS.VOTE_COLLECTOR_NAME')}
										name="vote_collector_id"
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
										onChange={(_, meta) => assemblyForm.setFieldValue('vote_collector_type', meta?.type)}
									/>
									<Form.Item name="vote_collector_type" hidden noStyle />
								</Col>

								{isChairmanRequired && (
									<Col span={24} md={8}>
										<SelectElement
											options={chairmanCandidates}
											label={t('ACTIONS.PRESIDENT_OF_ASSEMBLY_NAME')}
											name="chairman_id"
											rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
											onChange={(_, meta) => assemblyForm.setFieldValue('chairman_type', meta?.type)}
										/>
										<Form.Item name="chairman_type" hidden noStyle />
									</Col>
								)}
							</Row>

							<Divider />

							<div>
								<h6 className="mt-3 mb-2">{t('ACTIONS.LOCATION')}</h6>
								<div className="mb-3">
									<Checkbox
										checked={assemblyLocation.includes('Online')}
										className="compoundCheckbox"
										onChange={(e) => onChangeAssemblyLocation('Online', e.target.checked)}>
										<Flex align="center" gap={5}>
											<p className="fw-medium mb-0">{t('ACTIONS.ONLINE')} - </p> <img src={googleMeetLogo} style={{ height: 25 }} />
										</Flex>
										<span className="text-light">{t('ACTIONS.ONLINE_MEETING')}</span>
									</Checkbox>
								</div>
								<div>
									<Checkbox className="compoundCheckbox" onChange={(e) => onChangeAssemblyLocation('Physical', e.target.checked)}>
										<div>
											<p className="fw-medium mb-0">{t('ACTIONS.PHYSICAL')} </p>
											<span className="text-light">{t('ACTIONS.PHYSICAL_MEETING')}</span>
										</div>
									</Checkbox>
								</div>
								<Form.Item
									name="location_type"
									initialValue={assemblyLocation}
									rules={[{ required: true, message: t('ACTIONS.SELECT_AT_LEAST_ONE_LOCATION') }]}>
									<Input hidden />
								</Form.Item>
							</div>

							{assemblyLocation.includes('Physical') && (
								<div className="my-4">
									<MapComponent
										onSelect={(lat, lng, address) => {
											assemblyForm.setFieldsValue({ physical_lat: lat, physical_lng: lng, physical_address: address });
										}}
									/>

									<div className="mt-3">
										<Form.Item name="physical_lat" hidden rules={[{ required: assemblyLocation.includes('Physical') }]}>
											<Input hidden />
										</Form.Item>
										<Form.Item name="physical_lng" hidden rules={[{ required: assemblyLocation.includes('Physical') }]}>
											<Input hidden />
										</Form.Item>

										<FormElement
											fullWidth
											name="physical_address"
											label={t('ACTIONS.ADDRESS')}
											type="text"
											rules={[{ required: assemblyLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
										/>

										<FormElement
											fullWidth
											name="physical_address_description"
											label={t('ACTIONS.ADDRESS_DESC')}
											type="text"
											rules={[{ required: assemblyLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
										/>
									</div>
								</div>
							)}

							<Divider />

							<ComponentHeader title={t('ACTIONS.AGENDA_FOR_MEETING')} subtitle={t('ACTIONS.ADD_ALL_FILES_TO_ASSEMBLY')} />

							<FormListFilesElement form={assemblyForm} fieldName="files" />

							<Divider />

							<AssemblyPointsElement form={assemblyForm} />

							<Col span={24}>
								<Flex justify="flex-end" align="center" gap={20} className="mt-5">
									<ActionCostComponent serviceCost={serviceCost!} />

									<Button type="primary" htmlType="submit">
										{t('COMMON.CONFIRM')}
									</Button>
								</Flex>
							</Col>
						</Form>
					</Card>

					<VerificationModal
						open={verificationModalOpen}
						note={
							dayjs(assemblyForm.getFieldValue('datetime')).isBefore(dayjs().add(21, 'day')) ? t('ACTIONS.ASSEMBLY_LESS_THAN_21_DAYS_WARNING') : null
						}
						noteType="red"
						isLoading={isSubmitting}
						onCancel={() => setVerificationModalOpen(false)}
						onVerificationSuccess={onSubmit}
						module="assembly"
					/>
				</>
			)}
		</>
	);
};

export default ActionAssembliesCreatePage;
